import type { RefObject } from 'react';
import { useCallback } from 'react';

import { Permission } from '@/entities/permissions';
import type { SymbolKey } from '@/entities/symbol';
import { usePermission } from '@/features/_shared/user';
import { useChartContext } from '@/features/charts/context/ChartContext';
import type {
  ChartRef,
  ChartType,
  ExtendedSeriesOptionsType
} from '@/features/charts/types/highcharts';
import {
  addSymbols,
  requestSpotSelection,
  setSelectorMode,
  FORWARD_CURVE_PERIOD_WILDCARD,
  SELECTOR_MODES
} from '@/features/symbol-selector';
import { useLightMode } from '@/shared/lib/LightModeContext';
import { useAppDispatch } from '@/store/hooks';
import { openExportDrawer } from '@/store/slices/drawer.slice';
import { setExport } from '@/store/slices/export.slice';
import { ExportSelectionSource } from '../../hooks/useOpenExportSelection';

type ChartWithRangeSelector = ChartRef['chart'] & {
  rangeSelector?: {
    selected?: number;
    buttonOptions: Array<{ text?: string }>;
  };
};

const getSelectedTimeRange = (chartRef: RefObject<ChartRef | null>): string | undefined => {
  if (!chartRef.current?.chart) return undefined;
  const chart = chartRef.current.chart as ChartWithRangeSelector;
  const rangeSelector = chart.rangeSelector;
  const index = rangeSelector?.selected;
  if (index === undefined) return undefined;
  const buttonConfig = rangeSelector?.buttonOptions[index];
  if (!buttonConfig || buttonConfig.text === 'Max') return undefined;
  return buttonConfig.text;
};

const toSymbolKey =
  (defaultPeriod: string) =>
  (series: ExtendedSeriesOptionsType): SymbolKey => ({
    Code: series.custom.Code,
    Period: series.custom.Period || defaultPeriod,
    PeriodType: series.custom.PeriodType,
    TimeRef: series.custom.TimeRef,
    transactionDate: series.custom.transactionDate
  });

const toSeriesColor = (series: ExtendedSeriesOptionsType): string | undefined =>
  'color' in series && typeof series.color === 'string' ? series.color : undefined;

export function useExportChartAction(options: {
  disabled?: boolean;
  graphType: ChartType | 'fullscreen';
  isFullScreen?: boolean;
  metadata?: Record<string, string>;
  tabName?: string;
}): { disabled: boolean; exportChart: () => void } {
  const { disabled = false, graphType, isFullScreen = false, metadata, tabName } = options;
  const { hasPermission } = usePermission();
  const { chartSeries, chartRef } = useChartContext();
  const { isLightMode } = useLightMode();
  const dispatch = useAppDispatch();
  const type = graphType === 'spot' && isFullScreen ? 'fullscreen' : graphType || 'spot';

  const exportChart = useCallback(() => {
    if (!chartSeries) return;

    // Forward-curve chart series don't carry Period in their key (the widget
    // represents the whole curve). Use the wildcard so the selector-level
    // resolver can expand it to all periods via the forward catalog.
    const defaultPeriod = type === 'forwardSimple' ? FORWARD_CURVE_PERIOD_WILDCARD : '';
    const selectedSymbols = chartSeries.map(toSymbolKey(defaultPeriod));
    const selectedTimeRange = getSelectedTimeRange(chartRef);
    const colors = chartSeries
      .map(toSeriesColor)
      .filter((color): color is string => Boolean(color));

    if (type === 'forwardSimple') {
      dispatch(setSelectorMode(SELECTOR_MODES.FORWARD_CURVE));
      dispatch(addSymbols({ symbols: selectedSymbols }));
    } else {
      dispatch(requestSpotSelection({ symbols: selectedSymbols }));
    }
    // setExport must be dispatched before openExportDrawer so that exportNameBase
    // is already in Redux state when the drawer mounts and reads it.
    dispatch(
      setExport({
        exportType: type,
        from: metadata?.from,
        isLightMode,
        selectedTimeRange,
        exportNameBase: tabName,
        seriesColors: colors.length ? colors : undefined
      })
    );
    dispatch(
      openExportDrawer({ showHistory: false, source: ExportSelectionSource.CHART_WIDGET_BUTTON })
    );
  }, [chartRef, chartSeries, dispatch, isLightMode, metadata?.from, tabName, type]);

  return {
    disabled: disabled || !hasPermission(Permission.ExportImage),
    exportChart
  };
}
